import type * as Immutable from 'immutable';
import { useEffect, useMemo, useState } from 'react';
import type { Key, Selection } from 'react-aria-components';
import { useDispatch, useSelector } from 'react-redux';

import type { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { getElementId, getIconId, getTitle } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import type { ElementIcon } from '../../../../../../components/elementIcon/elementIconTypes';
import type { ThunkAwareDispatch } from '../../../../../../types/reduxTypes';
import { addRecentIconSearch, fetchIconSearch, setSelectedElementsIcon } from './iconPickerActions';
import {
    CHARACTER_ICONS,
    LETTERS_ANALYTICS_QUERY,
    NUMBER_ICONS,
    RECOMMENDED_SEARCH_ICON_COUNT,
    RECOMMENDED_TAB_ID,
} from './iconPickerConstants';
import { selectIconSearches, selectRecentIconSearches } from './iconPickerSelectors';
import type { IconPickerTab, IconSearchStatus } from './iconPickerTypes';
import {
    buildIconPickerTabs,
    getSearchTabId,
    getSharedIconId,
    getTitleLetterIcons,
    normaliseIconQuery,
    uniqueIcons,
} from './iconPickerUtils';

/**
 * The query a tab fetches icons for. Letters need no fetch, and nor does recommended for an untitled board.
 */
const getTabQuery = (tab: IconPickerTab, title: string): string | null => {
    if (tab.type === 'search') return tab.query;
    if (tab.type === 'letters') return null;

    return normaliseIconQuery(title) || null;
};

/**
 * Board icon picker state and handlers: tabs, search, the selected tab's icons and applying an icon.
 * State lives in the popover content, so the tab and search text reset each time it opens.
 */
const useIconPicker = (selectedElements: Immutable.List<ImMNElement>) => {
    const dispatch = useDispatch<ThunkAwareDispatch>();
    const searches = useSelector(selectIconSearches);
    const recentSearches = useSelector(selectRecentIconSearches);
    const [selectedTabId, setSelectedTabId] = useState<string>(RECOMMENDED_TAB_ID);
    const [searchText, setSearchText] = useState('');

    const firstElement = selectedElements.first();
    const elementId: string = getElementId(firstElement);
    const title: string = getTitle(firstElement) ?? '';
    const elementRecentSearches = recentSearches[elementId];

    const tabs = useMemo(() => buildIconPickerTabs(title, elementRecentSearches ?? []), [title, elementRecentSearches]);
    const selectedTab = tabs.find((tab) => tab.id === selectedTabId) ?? tabs[0];
    const query = getTabQuery(selectedTab, title);
    const search = query ? searches[query] : undefined;

    useEffect(() => {
        if (!query) return;

        dispatch(fetchIconSearch(query));
    }, [dispatch, query]);

    let status: IconSearchStatus = 'success';
    if (query) status = search?.status ?? 'loading';

    const icons = useMemo((): ElementIcon[] => {
        const searchIcons = search?.icons ?? [];
        const letterIcons = getTitleLetterIcons(title);

        if (selectedTab.type === 'search') return searchIcons;
        if (selectedTab.type === 'letters') return uniqueIcons([...letterIcons, ...NUMBER_ICONS, ...CHARACTER_ICONS]);
        if (status === 'loading') return [];

        const recommendedSearchIcons = searchIcons.slice(0, RECOMMENDED_SEARCH_ICON_COUNT);
        return uniqueIcons([...recommendedSearchIcons, ...letterIcons, ...NUMBER_ICONS]);
    }, [search, selectedTab.type, status, title]);

    const iconIds = selectedElements.map(getIconId).toArray();
    const sharedIconId = getSharedIconId(iconIds);
    const selectedIconKeys = new Set(sharedIconId ? [sharedIconId] : []);

    const selectTab = (tabId: Key) => setSelectedTabId(String(tabId));

    const showRecommended = () => setSelectedTabId(RECOMMENDED_TAB_ID);

    const retry = () => {
        if (!query) return;

        dispatch(fetchIconSearch(query));
    };

    const onSearchSubmit = (event: Pick<React.FormEvent, 'preventDefault'>) => {
        event.preventDefault();

        const submittedQuery = normaliseIconQuery(searchText);
        if (!submittedQuery) return;

        dispatch(addRecentIconSearch(elementId, submittedQuery));
        setSelectedTabId(getSearchTabId(submittedQuery));
        setSearchText('');
    };

    const onIconSelectionChange = (keys: Selection) => {
        if (keys === 'all') return;

        const [iconId] = keys;
        const icon = icons.find((tabIcon) => tabIcon.id === iconId);
        if (!icon) return;

        const searchQuery = selectedTab.type === 'letters' ? LETTERS_ANALYTICS_QUERY : (query ?? title);
        dispatch(setSelectedElementsIcon({ icon, selectedElements, searchQuery }));
    };

    return {
        tabs,
        selectedTab,
        selectTab,
        searchText,
        setSearchText,
        onSearchSubmit,
        status,
        icons,
        retry,
        showRecommended,
        selectedIconKeys,
        onIconSelectionChange,
    };
};

export default useIconPicker;
